import React from 'react';
import clsx from 'clsx';
import { useDropDown } from '../../hooks/useDropDown';
import './index.css';
import { MktAccordionVariant, MktAccordionType } from './Accordion';

export interface MktAccordionTabProps {
  isLast?: boolean;
  isActive: boolean;
  title: string;
  description: string;
  redirectUrl?: string;
  image: string;
  onClick: () => void;
  variant: `${MktAccordionVariant}`;
  disabledMobileImage: boolean;
  type: `${MktAccordionType}`;
}

export const MktAccordionTab: React.FC<MktAccordionTabProps> = ({
  isLast = false,
  isActive,
  description,
  image,
  redirectUrl,
  title,
  variant,
  disabledMobileImage,
  onClick,
  type,
}) => {
  const { containerRef, linkRef } = useDropDown({ expanded: isActive });
  const isLight = variant === MktAccordionVariant.light;
  return (
    <div
      className="wh-group wh-mx-auto wh-w-[312px] wh-cursor-pointer lg:wh-mx-0 lg:wh-w-full "
      aria-expanded={isActive}
      onClick={onClick}
    >
      <div
        className={clsx(
          'wh-mb-5 wh-flex wh-flex-col wh-gap-3 lg:wh-w-full xl:wh-w-[580px]',
          {
            'wh-w-full lg:wh-w-full xl:wh-w-full':
              type == MktAccordionType.simple,
          },
        )}
      >
        <div className="wh-flex wh-items-center wh-justify-between">
          <h1
            className={clsx(
              isActive
                ? 'wh-text-[24px] wh-font-[500] wh-leading-[31.2px] wh-tracking-[0.12px] lg:wh-text-[40px] lg:wh-leading-[48px]'
                : 'wh-text-[24px] wh-font-[400]  wh-leading-[31.2px] wh-tracking-[0.12px] lg:wh-font-[500] lg:wh-max-w-[90%] wh-max-w-[218px]',
              isLight
                ? 'wh-text-primary-900 group-aria-expanded:wh-max-w-full group-aria-expanded:wh-text-grey'
                : 'group-aria-expanded:wh-white wh-font-[400] wh-text-white group-aria-expanded:wh-max-w-full lg:wh-max-w-full',
              {
                'wh-font-bold': type == MktAccordionType.simple,
              },
            )}
          >
            {title}
          </h1>
          {!isActive && (
            <div className="wh-relative wh-mr-[17.98px] wh-flex wh-h-[24px] wh-w-[24px] wh-items-center wh-justify-center">
              <button
                className={clsx(
                  'wh-flex wh-h-[24px] wh-w-[24px] wh-items-center wh-justify-center wh-rounded-full',
                  `${isLight ? 'wh-bg-gold' : 'wh-bg-blue-light'}`,
                )}
                type="button"
              >
                <i
                  className={`wh-icon wh-icon-add add-icon wh-flex wh-w-[40px] wh-items-center wh-justify-center wh-pb-[0.5px] wh-text-[18px] ${
                    isLight ? ' wh-text-white' : 'wh-text-blue-dark'
                  } `}
                />
              </button>
            </div>
          )}
        </div>
        <div
          ref={containerRef}
          className={clsx(
            'wh-flex wh-flex-col wh-overflow-hidden',
            'wh-h-auto',
            'wh-transition-all',
          )}
        >
          <ul ref={linkRef}>
            <li className="wh-mb-[25px] wh-flex wh-flex-col wh-gap-3">
              <p
                className={clsx(
                  `wh-w-[274px] wh-text-base wh-font-regular wh-tracking-[0.08px] lg:wh-tracking-[0.16px] ${
                    isLight ? 'wh-text-grey' : 'wh-text-white'
                  }  lg:wh-w-[472px] lg:wh-leading-[28.8px]`,
                  {
                    'lg:wh-w-full xl:wh-w-full':
                      type == MktAccordionType.simple,
                  },
                )}
              >
                {description}
              </p>
              {redirectUrl && (
                <a
                  href={redirectUrl}
                  target="_blank"
                  className={` wh-text-md wh-leading-[32px] wh-tracking-[0.08px] wh-underline lg:wh-leading-[20.8px] lg:wh-tracking-[0.32px] lg:wh-no-underline wh-font-medium ${
                    isLight ? 'wh-text-grey' : 'wh-text-blue-light '
                  }  `}
                  rel="noreferrer"
                >
                  Learn More →
                </a>
              )}
            </li>

            {type !== MktAccordionType.simple && (
              <li
                className={`${
                  disabledMobileImage ? 'wh-hidden' : 'wh-flex'
                } wh-mb-[16px]  wh-h-[238px] wh-w-full wh-justify-center lg:wh-hidden`}
              >
                <img
                  className="wh-block wh-h-full wh-w-full wh-object-contain"
                  src={image}
                  alt={title}
                  loading="lazy"
                />
              </li>
            )}
          </ul>
        </div>
        {!isLast && (
          <div
            className={clsx(
              'wh-w-full wh-bg-gold-light xs:wh-h-[1px] lg:wh-h-[2px] lg:wh-w-full xl:wh-w-[571px]',
              {
                'wh-w-full lg:wh-w-full xl:wh-w-full':
                  type == MktAccordionType.simple,
              },
            )}
          />
        )}
      </div>
    </div>
  );
};
